import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@material-ui/core';
import { updateProjectAndCampaignStatus } from '../Dashboard/dashboardAPI';
import { useStyles } from './Styles';

const UpdateStatusModal = ({ open, onClose, projectData, onRefresh, setSnackbar }) => {
  const classes = useStyles();
  
  const [newProjectStatus, setNewProjectStatus] = useState('Completed');
  const [newCampaignStatus, setNewCampaignStatus] = useState('Complete');
  const [initialProjectStatus, setInitialProjectStatus] = useState('Completed');
  const [initialCampaignStatus, setInitialCampaignStatus] = useState('Complete');
  const [isSaving, setIsSaving] = useState(false);

  // Project Status options - from Project Status Selection
  const projectStatusOptions = ['Created', 'Initiated', 'In-Progress', 'Completed', 'On-Hold', 'Cancelled', 'Closed'];
  
  // Campaign Status options - from Campaign Status Selection
  const campaignStatusOptions = ['Ongoing', 'Complete', 'Delayed', 'Pending', 'Inactive'];

  // Reset to initial values when modal opens/closes or projectData changes
  useEffect(() => {
    if (open && projectData) {
      // Get status from projectData, validate it exists in options, otherwise use default
      const projectStatusFromData = projectData?.projectStatus;
      const campaignStatusFromData = projectData?.campaignStatus;
      
      // Validate project status exists in options, otherwise default to 'Completed'
      const currentProjectStatus = (projectStatusFromData && projectStatusOptions.includes(projectStatusFromData)) 
        ? projectStatusFromData 
        : 'Completed';
      
      // Validate campaign status exists in options, otherwise default to 'Complete'
      // Also handle case where API might return "Completed" instead of "Complete"
      let currentCampaignStatus = 'Complete';
      if (campaignStatusFromData) {
        if (campaignStatusOptions.includes(campaignStatusFromData)) {
          currentCampaignStatus = campaignStatusFromData;
        } else if (campaignStatusFromData === 'Completed') {
          // Map "Completed" to "Complete" for campaign status
          currentCampaignStatus = 'Complete';
        }
      }
      
      setInitialProjectStatus(currentProjectStatus);
      setInitialCampaignStatus(currentCampaignStatus);
      setNewProjectStatus(currentProjectStatus);
      setNewCampaignStatus(currentCampaignStatus);
    }
  }, [open, projectData]);

  // Check if any changes were made
  const hasChanges = 
    newProjectStatus !== initialProjectStatus || 
    newCampaignStatus !== initialCampaignStatus;

  const handleClose = () => {
    // Reset to initial values on close
    setNewProjectStatus(initialProjectStatus);
    setNewCampaignStatus(initialCampaignStatus);
    setIsSaving(false);
    onClose();
  };

  const handleSave = async () => {
    if (!hasChanges || isSaving) return;

    setIsSaving(true);
    try {
      // Determine what changed and prepare single payload
      const projectChanged = newProjectStatus !== initialProjectStatus;
      const campaignChanged = newCampaignStatus !== initialCampaignStatus;

      // Determine statusType based on what changed
      let statusType;
      if (projectChanged && campaignChanged) {
        statusType = 'BOTH';
      } else if (projectChanged) {
        statusType = 'PROJECT';
      } else if (campaignChanged) {
        statusType = 'CAMPAIGN';
      } else {
        // No changes (should not happen due to hasChanges check, but safety check)
        return;
      }

      // Prepare single payload
      const payload = {
        statusType: statusType,
      };

      if (statusType === 'PROJECT' || statusType === 'BOTH') {
        payload.projectId = projectData?.projectId || projectData?.id;
        payload.projectStatus = newProjectStatus;
      }

      if (statusType === 'CAMPAIGN' || statusType === 'BOTH') {
        payload.campaignId = projectData?.campaignId;
        payload.campaignStatus = newCampaignStatus;
      }

      console.log('========================================');
      console.log('📤 SINGLE API CALL: Updating Status');
      console.log('API Endpoint: POST /{appName}/{network}/{collabType}/{roleName}/updateProjectAndCampaignStatus');
      console.log('Status Type:', statusType);
      console.log('Payload:', JSON.stringify(payload, null, 2));
      console.log('========================================');

      // Execute single API call
      const result = await updateProjectAndCampaignStatus(payload);

      if (result?.status === true) {
        // Show success message
        const successMessage = statusType === 'BOTH'
          ? 'Project and Campaign status updated successfully'
          : statusType === 'PROJECT'
          ? 'Project status updated successfully'
          : 'Campaign status updated successfully';
        
        if (setSnackbar) {
          setSnackbar({
            open: true,
            message: successMessage,
            severity: 'success',
          });
        }

        // Refresh dashboard data to show updated statuses
        if (onRefresh) {
          await onRefresh();
        }

        // Close modal on success
        handleClose();
      } else {
        throw new Error(result?.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      
      // Show error message
      if (setSnackbar) {
        setSnackbar({
          open: true,
          message: error?.message || 'Failed to update status. Please try again.',
          severity: 'error',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      // Project Status colors
      case 'Completed':
        return '#4CAF50';
      case 'Initiated':
      case 'In-Progress':
        return '#FFA500';
      case 'On-Hold':
        return '#9C27B0';
      case 'Cancelled':
        return '#FF0000';
      case 'Closed':
        return '#707070';
      case 'Created':
        return '#A9A9A9';
      // Campaign Status colors
      case 'Complete':
        return '#4CAF50';
      case 'Ongoing':
        return '#FFA500';
      case 'Delayed':
        return '#9C27B0';
      case 'Pending':
        return '#800000';
      case 'Inactive':
        return '#A9A9A9';
      default:
        return '#4CAF50';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6" style={{ fontWeight: 600, fontSize: '20px' }}>
          Update Project {projectData?.projectId || 'XYZ682'}
        </Typography>
        <Typography className={classes.subtitle}>Update Status</Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Table className={classes.statusTable}>
          <TableBody>
            <TableRow>
              <TableCell className={classes.tableHeader}>Current Project Status</TableCell>
              <TableCell className={classes.tableHeader} style={{ textAlign: 'center' }}>New Status</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Chip
                  label={initialProjectStatus || 'Completed'}
                  className={classes.statusChip}
                  style={{
                    backgroundColor: getStatusChipColor(initialProjectStatus),
                  }}
                />
              </TableCell>
              <TableCell>
                <Box className={classes.selectWrapper}>
                  <FormControl variant="outlined" size="small" className={classes.select}>
                    <Select
                      value={newProjectStatus}
                      onChange={(e) => setNewProjectStatus(e.target.value)}
                    >
                      {projectStatusOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Table className={classes.statusTable}>
          <TableBody>
            <TableRow>
              <TableCell className={classes.tableHeader}>Current Campaign Status</TableCell>
              <TableCell className={classes.tableHeader} style={{ textAlign: 'center' }}>New Status</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Chip
                  label={initialCampaignStatus || 'Complete'}
                  className={classes.statusChip}
                  style={{
                    backgroundColor: getStatusChipColor(initialCampaignStatus),
                  }}
                />
              </TableCell>
              <TableCell>
                <Box className={classes.selectWrapper}>
                  <FormControl variant="outlined" size="small" className={classes.select}>
                    <Select
                      value={newCampaignStatus}
                      onChange={(e) => setNewCampaignStatus(e.target.value)}
                    >
                      {campaignStatusOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'center', padding: '16px 24px 24px 24px' }}>
        <Button
          onClick={handleClose}
          className={classes.closeButton}
          disabled={isSaving}
        >
          Close
        </Button>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className={classes.saveButton}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateStatusModal;


